import * as React from "react";
import { Card, Row, Col } from "react-bootstrap";
import { useLocation, Link } from "react-router-dom";
import { useQuery } from "@apollo/client";

import {
  GET_PAGE_SEARCH,
  GET_PAKIHI,
  GET_PANUI,
  PageSearchData,
  PanuiQueryData,
  PakihiQueryData,
} from "@plinknz/tah-website-elements";

import { Hero } from "../components/hero";
import { Loader } from "../components/loader";
import {
  getPakihiPage,
  searchPages,
  SearchResult,
  searchPanui,
  searchPakihi,
} from "../utility/search";

export const SearchResults: React.FunctionComponent = () => {
  const { useEffect, useState } = React;
  const { pathname } = useLocation();
  const searchString = pathname.replace("/search-results/", "");
  const { loading, data, error: pageError } = useQuery<{
    pages: PageSearchData[];
  }>(GET_PAGE_SEARCH);
  const {
    loading: loadingPakihi,
    data: pakihiData,
    error: pakihiError,
  } = useQuery<PakihiQueryData>(GET_PAKIHI);
  const {
    loading: loadingPanui,
    data: panuiData,
    error: panuiError,
  } = useQuery<PanuiQueryData>(GET_PANUI, {
    variables: {
      sort: "",
      filters: {},
      pagination: { limit: -1 },
    },
  });
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [pageSearchResults, setPageSearchResults] = useState<SearchResult[]>(
    []
  );
  const [panuiSearchResults, setPanuiSearchResults] = useState<SearchResult[]>(
    []
  );
  const [pakihiSearchResults, setPakihiSearchResults] = useState<
    SearchResult[]
  >([]);
  const [pakihiSlug, setPakihiSlug] = useState("");

  function runSearch() {
    const pages = searchPages({ searchString, pages: data?.pages });
    setPageSearchResults(pages);
  }

  function runPanuiSearch() {
    const panui = searchPanui({
      searchString,
      newsItems: panuiData?.newsItems,
    });
    setPanuiSearchResults(panui);
  }

  function runPakihiSearch() {
    const pakihi = searchPakihi({
      searchString,
      pakihis: pakihiData?.pakihis,
      slug: "zzz",
    });
    setPakihiSearchResults(pakihi);
  }

  function slugToBreadcrumb(slug: string) {
    if (!slug.match(/\//g) || slug.match(/\//g)?.length < 2) {
      return "";
    }

    return slug.replace(/^\//, "").replace(/\//g, " > ");
  }

  useEffect(() => {
    if (data?.pages) {
      if (data.pages.length) {
        runSearch();
        const existingPakihiPage = getPakihiPage(data?.pages);
        setPakihiSlug(existingPakihiPage);
      }
    }
  }, [data]);

  useEffect(() => {
    if (panuiData?.newsItems) {
      if (panuiData.newsItems.length) {
        runPanuiSearch();
      }
    }
  }, [panuiData]);

  useEffect(() => {
    if (pakihiData?.pakihis) {
      if (pakihiData.pakihis.length) {
        runPakihiSearch();
      }
    }
  }, [pakihiData]);

  useEffect(() => {
    let newSearchResults = [...pageSearchResults, ...panuiSearchResults];
    if (pakihiSlug && pakihiSearchResults.length) {
      const newPakihiSR = pakihiSearchResults.map((page) => ({
        ...page,
        slug: pakihiSlug,
      }));

      newSearchResults = [...newSearchResults, ...newPakihiSR];
    }
    setSearchResults(
      newSearchResults.sort((a, b) => {
        if (a.match.matchCount < b.match.matchCount) {
          return 1;
        }

        if (a.match.matchCount > b.match.matchCount) {
          return -1;
        }

        return 0;
      })
    );
  }, [pageSearchResults, panuiSearchResults, pakihiSearchResults, pakihiSlug]);

  useEffect(() => {
    if (searchResults.length) {
      setSearchResults([]);
      setPageSearchResults([]);
      setPanuiSearchResults([]);
      setPakihiSearchResults([]);

      runSearch();
      runPanuiSearch();
      runPakihiSearch();
    }
  }, [searchString]);

  const error = pageError || panuiError || pakihiError;
  if (error) {
    console.warn(error);
    const errorMessage = error?.graphQLErrors
      .map((graphqlError) =>
        graphqlError.extensions?.exception
          ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access
            `${graphqlError.extensions.exception.output.statusCode} ${graphqlError.extensions.exception.message}`
          : null
      )
      .filter(Boolean)
      .join(", ");

    return (
      <Hero title="There was an unexpected error" subtitle={errorMessage} />
    );
  }

  return (
    <>
      <Hero title="Search Results" subtitle="Kua Rapua" />
      <div className="content || constrain-width">
        {(loading || loadingPanui || loadingPakihi) && (
          <div className="search-load-header">
            <h3>Loading</h3>
            <div className="splash">
              <Loader size="large" />
            </div>
          </div>
        )}
        {!loading && !loadingPanui && !loadingPakihi && (
          <div data-testid="search-result-container">
            <h3 data-testid="search-result-heading">
              {searchResults.length
                ? "Search results for "
                : "Could not find anything for "}
              "{searchString}"
            </h3>
            <Row>
              {searchResults.map(
                ({ title, slug, match: { pre, match, post, matchCount } }) => (
                  <Col key={title}>
                    <Link
                      to={slug}
                      title={`${matchCount} match${matchCount > 1 ? "es" : ""}`}
                      className="card-link"
                    >
                      <Card>
                        <Card.Body>
                          <Card.Header as="h5">{title}</Card.Header>
                          <Card.Subtitle>
                            {slugToBreadcrumb(slug)}
                          </Card.Subtitle>
                          <Card.Text>
                            {pre}
                            <span className="search-highlight">{match}</span>
                            {post}
                          </Card.Text>
                        </Card.Body>
                      </Card>
                    </Link>
                  </Col>
                )
              )}
            </Row>
          </div>
        )}
      </div>
    </>
  );
};
